import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  cloudListProjects,
  getOpsTenantId,
  isCloudProjectId,
  mapApiProject,
} from "../api/client";
import { useAuth } from "../auth/AuthContext";
import type { Project } from "./mock";

interface ProjectsState {
  list: Project[];
  cloudReady: boolean;
  syncing: boolean;
  add: (project: Project) => void;
  update: (project: Project) => void;
  upsert: (project: Project) => void;
  remove: (id: string) => void;
  getById: (id: string) => Project | undefined;
  refreshFromCloud: () => Promise<void>;
  isCloudBacked: (id: string) => boolean;
}

const ProjectsContext = createContext<ProjectsState | null>(null);

export function ProjectsProvider({ children }: { children: ReactNode }) {
  const { email, isAuthenticated } = useAuth();
  const [list, setList] = useState<Project[]>([]);
  const [cloudReady, setCloudReady] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [cloudIds, setCloudIds] = useState<Set<string>>(new Set());

  const mergeCloudItems = useCallback((items: ReturnType<typeof mapApiProject>[]) => {
    const ids = new Set(items.map((p) => p.id));
    setCloudIds((prevIds) => {
      const next = new Set(ids);
      // 运营跨租户：列表 API 仍是平台租户（常为空），不得冲掉已 upsert 的调试项目
      if (getOpsTenantId()) {
        for (const id of prevIds) next.add(id);
      }
      return next;
    });
    setList((prev) => {
      const mapped = items.map((api) => {
        const prevHit = prev.find((p) => p.id === api.id);
        return prevHit
          ? {
              ...api,
              master: prevHit.master,
              claimCode: prevHit.claimCode,
              deviceCount: prevHit.deviceCount ?? api.deviceCount,
              capabilities: prevHit.capabilities || api.capabilities,
            }
          : api;
      });
      if (!getOpsTenantId()) return mapped;
      const mappedIds = new Set(mapped.map((p) => p.id));
      const keep = prev.filter((p) => !mappedIds.has(p.id));
      return [...mapped, ...keep];
    });
  }, []);

  const refreshFromCloud = useCallback(async () => {
    if (!email) {
      setCloudReady(false);
      setList([]);
      return;
    }
    setSyncing(true);
    try {
      const { items } = await cloudListProjects();
      mergeCloudItems(items.map((p) => mapApiProject(p)));
      setCloudReady(true);
    } catch {
      // 列表失败不得清 token / 整表清空（否则运营进调试会被 15s 轮询踢出）
      setCloudReady(false);
    } finally {
      setSyncing(false);
    }
  }, [email, mergeCloudItems]);

  useEffect(() => {
    if (!isAuthenticated) {
      setList([]);
      setCloudReady(false);
      setCloudIds(new Set());
      return;
    }
    void refreshFromCloud();
    const t = window.setInterval(() => void refreshFromCloud(), 15000);
    return () => window.clearInterval(t);
  }, [isAuthenticated, refreshFromCloud]);

  const value = useMemo<ProjectsState>(
    () => ({
      list,
      cloudReady,
      syncing,
      add: (project) => {
        setList((prev) => [project, ...prev]);
        if (isCloudProjectId(project.id)) {
          setCloudIds((prev) => new Set(prev).add(project.id));
        }
      },
      update: (project) => {
        setList((prev) =>
          prev.map((p) => (p.id === project.id ? project : p)),
        );
        if (isCloudProjectId(project.id)) {
          setCloudIds((prev) => new Set(prev).add(project.id));
        }
      },
      upsert: (project) => {
        setList((prev) => {
          const idx = prev.findIndex((p) => p.id === project.id);
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = { ...next[idx], ...project };
            return next;
          }
          return [project, ...prev];
        });
        if (isCloudProjectId(project.id)) {
          setCloudIds((prev) => new Set(prev).add(project.id));
        }
      },
      remove: (id) => {
        setList((prev) => prev.filter((p) => p.id !== id));
        setCloudIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      },
      getById: (id) => list.find((p) => p.id === id),
      refreshFromCloud,
      isCloudBacked: (id) => cloudIds.has(id) || isCloudProjectId(id),
    }),
    [list, cloudReady, syncing, refreshFromCloud, cloudIds],
  );

  return (
    <ProjectsContext.Provider value={value}>{children}</ProjectsContext.Provider>
  );
}

export function useProjects() {
  const ctx = useContext(ProjectsContext);
  if (!ctx) {
    throw new Error("useProjects must be used within ProjectsProvider");
  }
  return ctx;
}
